// app/javascript/controllers/photo_modal_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "snapImage"]

  connect() {
    this._handlePopState = this._handlePopState.bind(this)
    window.addEventListener("popstate", this._handlePopState)

    this._setupVideoObserver()

    const url = new URL(window.location)
    const imageId = url.searchParams.get("image")
    if (imageId) {
      const baseUrl = new URL(url)
      baseUrl.searchParams.delete("image")
      history.replaceState(history.state, "", baseUrl)
      this._pushModalState(imageId)
      requestAnimationFrame(() => {
        this._openModalForImage(imageId)
      })
    }
  }

  disconnect() {
    window.removeEventListener("popstate", this._handlePopState)
    if (this.videoObserver) {
      this.videoObserver.disconnect()
    }
  }

  open(event) {
    const { imageId } = event.params
    if (this.dialogTarget.open && this.currentImageId === String(imageId))
      return

    this._pushModalState(imageId)
    this._openModalForImage(imageId)
  }

  close(_event) {
    if (this.dialogTarget.open) {
      history.back()
    }
  }

  _openModalForImage(imageId) {
    this.currentImageId = String(imageId)
    if (!this.dialogTarget.open) {
      this.dialogTarget.showModal()
    }
    const targetImage = this.snapImageTargets.find(
      (el) => el.dataset.imageId === String(imageId),
    )
    if (!targetImage) return

    const scrollContainer = this.dialogTarget.querySelector(".w-screen.overflow-y-scroll")
    const scrollToTarget = () => {
      const containerTop = scrollContainer.getBoundingClientRect().top
      const targetTop = targetImage.getBoundingClientRect().top
      scrollContainer.scrollTop += targetTop - containerTop
    }

    scrollToTarget()
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToTarget)
    })
  }

  _closeModalOnly() {
    if (this.dialogTarget.open) {
      this.dialogTarget.close()
    }
    this.currentImageId = null
  }

  _pushModalState(imageId) {
    const url = new URL(window.location)
    url.searchParams.set("image", imageId)
    history.pushState({ photoModal: true, imageId }, "", url)
  }

  _handlePopState(event) {
    const state = event.state
    if (state && state.photoModal) {
      this._openModalForImage(state.imageId)
    } else {
      this._closeModalOnly()
      const url = new URL(window.location)
      if (url.searchParams.has("image")) {
        url.searchParams.delete("image")
        history.replaceState(history.state, "", url)
      }
    }
  }

  _setupVideoObserver() {
    const options = {
      root: this.dialogTarget.querySelector(".w-screen.overflow-y-scroll"),
      threshold: 0.5,
    }

    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector("video")
        if (video) {
          if (!entry.isIntersecting) {
            video.pause()
          }
        }
      })
    }, options)

    this.snapImageTargets.forEach((snapImage) => {
      this.videoObserver.observe(snapImage)
    })
  }

  snapImageTargetConnected(target) {
    if (this.videoObserver) {
      this.videoObserver.observe(target)
    }
  }

  snapImageTargetDisconnected(target) {
    if (this.videoObserver) {
      this.videoObserver.unobserve(target)
    }
  }
}
